import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  selectAuth,
  selectUser,
  selectRole,
  selectPermissions,
  selectIsAuthenticated,
  selectToken,
  selectHasPermission,
  selectHasAnyPermission,
  selectHasRole,
  selectIsSuperAdmin,
  selectCanViewModule,
  selectCanAddToModule,
  selectCanEditInModule,
  selectCanDeleteInModule,
  selectCanViewUniversityDropdown,
  selectCanViewStudentDropdown,
  selectCanViewUserManagementDropdown,
  logout,
  refreshAuth
} from '../store/authSlice';
import { clearAuthData } from '../utils/authUtils';

// Main auth hook
export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  
  const handleLogout = useCallback(() => {
    clearAuthData();
    dispatch(logout());
  }, [dispatch]);
  
  const handleRefreshAuth = useCallback(() => {
    dispatch(refreshAuth());
  }, [dispatch]);
  
  return {
    ...auth,
    logout: handleLogout,
    refreshAuth: handleRefreshAuth
  };
};

// Permission hooks - strictly permission-based
export const usePermission = (permissionName) => {
  return useSelector(selectHasPermission(permissionName));
};

export const useAnyPermission = (permissionNames) => {
  return useSelector(selectHasAnyPermission(permissionNames));
};

export const useRole = (roleName) => {
  return useSelector(selectHasRole(roleName));
};

export const useIsSuperAdmin = () => {
  return useSelector(selectIsSuperAdmin);
};

// Module permission hooks - strictly permission-based
export const useCanViewModule = (module) => {
  return useSelector(selectCanViewModule(module));
};

export const useCanAddToModule = (module) => {
  return useSelector(selectCanAddToModule(module));
};

export const useCanEditInModule = (module) => {
  return useSelector(selectCanEditInModule(module));
};

export const useCanDeleteInModule = (module) => {
  return useSelector(selectCanDeleteInModule(module));
};

// Legacy hook for backward compatibility
export const useCanCreateInModule = (module) => {
  return useCanAddToModule(module);
};

// Dropdown permission hooks - strictly permission-based
export const useCanViewUniversityDropdown = () => {
  return useSelector(selectCanViewUniversityDropdown);
};

export const useCanViewStudentDropdown = () => {
  return useSelector(selectCanViewStudentDropdown);
};

export const useCanViewUserManagementDropdown = () => {
  return useSelector(selectCanViewUserManagementDropdown);
};

// Specific module permission hooks - strictly permission-based
export const useCanViewUniversity = () => {
  return usePermission('university.view');
};

export const useCanViewCountry = () => {
  return usePermission('country.view');
};

export const useCanViewCity = () => {
  return usePermission('city.view');
};

export const useCanViewUniversityProgram = () => {
  return usePermission('university_program.view');
};

export const useCanViewScholarship = () => {
  return usePermission('scholarship.view');
};

export const useCanViewStudent = () => {
  return usePermission('student.view');
};

export const useCanViewStudentRequest = () => {
  return usePermission('student_request.view');
};

export const useCanViewContract = () => {
  return usePermission('contract.view');
};

export const useCanViewCashManagement = () => {
  return usePermission('cash_management.view');
};

export const useCanViewConsultant = () => {
  return usePermission('consultant.view');
};

export const useCanViewUserManagement = () => {
  return usePermission('user_management.view');
};

export const useCanViewRoleManagement = () => {
  return usePermission('role_management.view');
};

export const useCanManagePermissions = () => {
  return usePermission('permission.management');
};

export const useCanViewTaskManagement = () => {
  return usePermission('task_management.view');
};

// Combined module permissions hook - strictly permission-based
export const useModulePermissions = (module) => {
  const canView = useCanViewModule(module);
  const canAdd = useCanAddToModule(module);
  const canEdit = useCanEditInModule(module);
  const canDelete = useCanDeleteInModule(module);
  
  return {
    canView,
    canAdd,
    canCreate: canAdd, // Legacy alias
    canEdit,
    canDelete,
    hasAnyPermission: canView || canAdd || canEdit || canDelete
  };
};

// User data hooks
export const useCurrentUser = () => {
  return useSelector(selectUser);
};

export const useCurrentRole = () => {
  return useSelector(selectRole);
};

export const usePermissions = () => {
  return useSelector(selectPermissions);
};

// Authentication status hooks
export const useIsAuthenticated = () => {
  return useSelector(selectIsAuthenticated);
};

export const useToken = () => {
  return useSelector(selectToken);
}; 